import React, { useRef, useEffect } from "react";
import InputWithChecks from "../../shared/InputWithChecks";
import PrimaryButton from "../../shared/PrimaryButton";
import PrimaryButtonDark from "../../shared/PrimaryButtonDark";
import InfoIcon from "../../shared/InfoIcon";
import "./ProductOptionsEditor.css";

/**
 * Herbruikbare component voor het beheren van productopties
 * @param {Array} productOptions - Array met huidige productopties
 * @param {Function} setProductOptions - Setter functie voor productopties
 * @param {Object} refs - Object met refs voor de verschillende optie-elementen
 */
const ProductOptionsEditor = ({ 
  productOptions, 
  setProductOptions, 
  refs = {} 
}) => {
  // Maak lokale refs aan als ze niet worden doorgegeven
  // Door refs altijd aan te maken, en dan eventueel de waarde van de prop te gebruiken, 
  // voorkomen we conditionale aanroepen van useRef
  const localOptionNameRefs = useRef([]);
  const localChoiceNameRefs = useRef([]);
  const localChoicePriceRefs = useRef([]);
  const localMaxSelectionsRefs = useRef([]);
  
  // Gebruik de doorgegeven refs als ze bestaan, anders de lokale
  const optionNameRefs = refs.optionNameRefs || localOptionNameRefs;
  const choiceNameRefs = refs.choiceNameRefs || localChoiceNameRefs;
  const choicePriceRefs = refs.choicePriceRefs || localChoicePriceRefs;
  const maxSelectionsRefs = refs.maxSelectionsRefs || localMaxSelectionsRefs;

  // Effect bijwerken voor de nieuwe refs-structuur
  useEffect(() => {
    // Initialize refs arrays
    optionNameRefs.current = optionNameRefs.current.slice(0, productOptions.length);
    choiceNameRefs.current = choiceNameRefs.current.slice(0, productOptions.length);
    choicePriceRefs.current = choicePriceRefs.current.slice(0, productOptions.length);
    maxSelectionsRefs.current = maxSelectionsRefs.current.slice(0, productOptions.length);
  }, [productOptions, optionNameRefs, choiceNameRefs, choicePriceRefs, maxSelectionsRefs]);

  // Functie om een nieuwe optie toe te voegen
  const addProductOption = () => {
    setProductOptions([
      ...productOptions,
      {
        name: "",
        type: "select",
        required: false,
        options: [{ name: "", price: 0 }],
        maxSelections: 1
      }
    ]);
  };

  // Functie om een optie te verwijderen
  const removeProductOption = (index) => {
    setProductOptions(productOptions.filter((_, i) => i !== index));
  };

  // Functie om een optie bij te werken
  const updateProductOption = (index, field, value) => {
    const newOptions = [...productOptions];
    if (field === "maxSelections") {
      // Zorg ervoor dat maxSelections minimaal 1 is en een nummer is
      const numValue = parseInt(value) || 1;
      newOptions[index] = { 
        ...newOptions[index], 
        [field]: Math.max(1, numValue)
      };
    } else {
      newOptions[index] = { ...newOptions[index], [field]: value };
    }
    setProductOptions(newOptions);
  };

  // Functie om een keuze toe te voegen aan een optie
  const addOptionChoice = (optionIndex) => {
    const newOptions = [...productOptions];
    newOptions[optionIndex].options.push({ name: "", price: 0 });
    setProductOptions(newOptions);
  };

  // Functie om een keuze te verwijderen
  const removeOptionChoice = (optionIndex, choiceIndex) => {
    const newOptions = [...productOptions];
    newOptions[optionIndex].options = newOptions[optionIndex].options.filter((_, i) => i !== choiceIndex);
    setProductOptions(newOptions);
  };

  // Functie om een keuze bij te werken
  const updateOptionChoice = (optionIndex, choiceIndex, field, value) => {
    const newOptions = [...productOptions];
    newOptions[optionIndex].options[choiceIndex] = {
      ...newOptions[optionIndex].options[choiceIndex],
      [field]: value
    };
    setProductOptions(newOptions);
  };

  return (
    <div className="product-options-container g-20">
      <div className="product-options-header g-10">
        <h3>Product Opties</h3>
        <p className="c-g">Voeg optionele keuzes toe voor dit product</p>
        <PrimaryButtonDark
          onClick={(e) => {
            e.preventDefault();
            addProductOption();
          }}
          className="secondary-button small-button"
        >
          <i className="fi fi-rs-plus"></i> Optie Toevoegen
        </PrimaryButtonDark>
      </div>
      
      {productOptions.map((option, optionIndex) => (
        <div key={`option-${optionIndex}`} className="product-option-item g-15">
          <div className="option-header g-10">
            <div className="g-1-1 a-i-e gap-1">
              <InputWithChecks
                ref={(el) => {
                  refs.optionNameRefs.current[optionIndex] = el;
                }}
                label={
                  <>
                    Optienaam {optionIndex + 1}
                    <InfoIcon info="Geef een duidelijke naam aan deze optiegroep (bijv. 'Saus kiezen' of 'Extra toppings')" />
                  </>
                }
                name={`option-name-${optionIndex}`}
                inputType="text"
                placeholder="Bijv. Saus kiezen"
                value={option.name}
                onChange={(e) => updateProductOption(optionIndex, "name", e.target.value)}
              />
              
              <div className="option-controls g-10">
                <label>
                  Type
                  <InfoIcon info="Kies tussen een enkele keuze (klant kiest 1 optie) of meerdere keuzes (klant kan meerdere opties selecteren)" />
                </label>
                <select
                  value={option.type}
                  onChange={(e) => updateProductOption(optionIndex, "type", e.target.value)}
                >
                  <option value="select">Enkele keuze</option>
                  <option value="multiselect">Meerdere keuzes</option>
                </select>
              </div>
              
              {option.type === "multiselect" && (
                <div className="g-10">
                  <label>
                    Maximum aantal selecties
                    <InfoIcon info="Bepaal hoeveel opties een klant maximaal kan selecteren uit deze groep" />
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={option.maxSelections}
                    onChange={(e) => updateProductOption(optionIndex, "maxSelections", e.target.value)}
                    ref={(el) => {
                      refs.maxSelectionsRefs.current[optionIndex] = el;
                    }}
                  />
                </div>
              )}
              
              <div className="option-controls g-10">
                <label>
                  Verplicht
                  <InfoIcon info="Moet de klant verplicht een optie kiezen voordat het product aan de winkelwagen kan worden toegevoegd?" />
                </label>
                <select
                  value={option.required ? "true" : "false"}
                  onChange={(e) => updateProductOption(optionIndex, "required", e.target.value === "true")}
                >
                  <option value="true">Ja</option>
                  <option value="false">Nee</option>
                </select>
              </div>
              
              <button
                type="button"
                className="delete-button small-button"
                onClick={() => removeProductOption(optionIndex)}
              >
                <i className="fi fi-rs-trash-xmark"></i> Verwijderen
              </button>
            </div>
          </div>
          
          <div className="option-choices g-10">
            <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
              <h4>Beschikbare keuzes</h4>
              <InfoIcon info="Voeg hier de individuele keuzes toe binnen deze optie, zoals 'Extra frietjes' of 'Pepersaus'." />
            </div>
            <div className="option-choices-list g-10">
              {option.options.map((choice, choiceIndex) => (
                <div key={`choice-${optionIndex}-${choiceIndex}`} className="option-choice-item g-1-1 a-c-s gap-1">
                  <InputWithChecks
                    ref={(el) => {
                      if (!choiceNameRefs.current[optionIndex]) {
                        choiceNameRefs.current[optionIndex] = [];
                      }
                      choiceNameRefs.current[optionIndex][choiceIndex] = el;
                    }}
                    label={`Keuze ${choiceIndex + 1}`}
                    name={`choice-name-${optionIndex}-${choiceIndex}`}
                    inputType="text"
                    placeholder="Bijv. Extra saus"
                    value={choice.name}
                    onChange={(e) => updateOptionChoice(optionIndex, choiceIndex, "name", e.target.value)}
                  />
                  
                  <InputWithChecks
                    ref={(el) => {
                      if (!choicePriceRefs.current[optionIndex]) {
                        choicePriceRefs.current[optionIndex] = [];
                      }
                      choicePriceRefs.current[optionIndex][choiceIndex] = el;
                    }}
                    label="Meerprijs"
                    name={`choice-price-${optionIndex}-${choiceIndex}`}
                    inputType="number"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    value={choice.price}
                    onChange={(e) => updateOptionChoice(optionIndex, choiceIndex, "price", e.target.value)}
                  />
                  
                  <button
                    type="button"
                    className="delete-button small-button"
                    onClick={() => removeOptionChoice(optionIndex, choiceIndex)}
                  >
                    <i className="fi fi-rs-trash-xmark"></i>
                  </button>
                </div>
              ))}
            </div>
            
            <PrimaryButtonDark
              type="button"
              className="add-choice-button small-button"
              onClick={() => addOptionChoice(optionIndex)}
            >
              <i className="fi fi-rs-plus"></i> Keuze Toevoegen
            </PrimaryButtonDark>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProductOptionsEditor; 